import type { GVector } from '../physics/index.ts';
import type { RidePeak, RideSummary } from '../ride/runner.ts';

export type SimulatedIntensityLevel =
  'comfortable' | 'noticeable' | 'intense' | 'extreme' | 'absurd';

export type SimulatedIntensityMetric =
  'speed' | 'positive_vertical_g' | 'negative_vertical_g' | 'lateral_g' | 'longitudinal_g';

export interface SimulatedIntensitySummary {
  level: SimulatedIntensityLevel;
  label: string;
  message: string;
}

export interface AuthoritativeIntensitySummary extends SimulatedIntensitySummary {
  strongestMetric: SimulatedIntensityMetric;
  strongestDistanceM: number;
  strongestCarIndex: number;
  sustainedSeconds: number | null;
}

export interface IntensityThresholdBands {
  readonly noticeable: number;
  readonly intense: number;
  readonly extreme: number;
  readonly absurd: number;
}

export interface IntensityThresholdConfig {
  readonly speed: IntensityThresholdBands;
  readonly positiveVerticalG: IntensityThresholdBands;
  readonly negativeVerticalG: IntensityThresholdBands;
  readonly absoluteLateralG: IntensityThresholdBands;
  readonly absoluteLongitudinalG: IntensityThresholdBands;
}

const SUMMARIES: Record<SimulatedIntensityLevel, SimulatedIntensitySummary> = {
  comfortable: {
    level: 'comfortable',
    label: 'Comfortable',
    message: 'Forces stay in the calmest simulated-intensity band.',
  },
  noticeable: {
    level: 'noticeable',
    label: 'Noticeable',
    message: 'Expect a few stronger moments.',
  },
  intense: {
    level: 'intense',
    label: 'Intense',
    message: 'Expect strong simulated forces.',
  },
  extreme: {
    level: 'extreme',
    label: 'Extreme',
    message: 'Expect extreme simulated forces.',
  },
  absurd: {
    level: 'absurd',
    label: 'Absurd',
    message: 'Expect deliberately absurd simulated forces.',
  },
};

export const INTENSITY_RANK: Readonly<Record<SimulatedIntensityLevel, number>> = {
  comfortable: 0,
  noticeable: 1,
  intense: 2,
  extreme: 3,
  absurd: 4,
};

export function intensitySummary(level: SimulatedIntensityLevel): SimulatedIntensitySummary {
  return SUMMARIES[level];
}

function classifyHigh(value: number, bands: IntensityThresholdBands): SimulatedIntensityLevel {
  if (value >= bands.absurd) return 'absurd';
  if (value >= bands.extreme) return 'extreme';
  if (value >= bands.intense) return 'intense';
  if (value >= bands.noticeable) return 'noticeable';
  return 'comfortable';
}

function classifyLow(value: number, bands: IntensityThresholdBands): SimulatedIntensityLevel {
  if (value <= bands.absurd) return 'absurd';
  if (value <= bands.extreme) return 'extreme';
  if (value <= bands.intense) return 'intense';
  if (value <= bands.noticeable) return 'noticeable';
  return 'comfortable';
}

export function promoteIntensity(level: SimulatedIntensityLevel): SimulatedIntensityLevel {
  return level === 'comfortable'
    ? 'noticeable'
    : level === 'noticeable'
      ? 'intense'
      : level === 'intense'
        ? 'extreme'
        : 'absurd';
}

export function classifySimulatedIntensity(vector: GVector): SimulatedIntensitySummary {
  const candidates: SimulatedIntensityLevel[] = [
    vector.vertical >= 0
      ? classifyHigh(vector.vertical, { noticeable: 4, intense: 5, extreme: 6, absurd: 8 })
      : classifyLow(vector.vertical, {
          noticeable: -0.5,
          intense: -1,
          extreme: -1.5,
          absurd: -3,
        }),
    classifyHigh(Math.abs(vector.lateral), {
      noticeable: 0.75,
      intense: 1.25,
      extreme: 2,
      absurd: 4,
    }),
    classifyHigh(Math.abs(vector.longitudinal), {
      noticeable: 0.75,
      intense: 1.25,
      extreme: 2,
      absurd: 4,
    }),
  ];
  const level = candidates.reduce((strongest, candidate) =>
    INTENSITY_RANK[candidate] > INTENSITY_RANK[strongest] ? candidate : strongest,
  );
  return SUMMARIES[level];
}

function longestSustainedSeconds(
  trace: RideSummary['trace'],
  predicate: (sample: RideSummary['trace'][number]) => boolean,
): number {
  let start: number | null = null;
  let longest = 0;
  for (const sample of trace) {
    if (predicate(sample)) {
      start ??= sample.time;
      longest = Math.max(longest, sample.time - start);
    } else start = null;
  }
  return longest;
}

export function summarizeRideIntensity(
  summary: RideSummary,
  thresholds: IntensityThresholdConfig,
): AuthoritativeIntensitySummary {
  const candidates: Array<{
    metric: SimulatedIntensityMetric;
    peak: RidePeak;
    level: SimulatedIntensityLevel;
    bands: IntensityThresholdBands;
    sampleValue: (sample: RideSummary['trace'][number]) => number;
    low: boolean;
  }> = [
    {
      metric: 'speed',
      peak: summary.topSpeed,
      level: classifyHigh(summary.topSpeed.value, thresholds.speed),
      bands: thresholds.speed,
      sampleValue: (sample) => sample.speed,
      low: false,
    },
    {
      metric: 'positive_vertical_g',
      peak: summary.maxVerticalG,
      level: classifyHigh(summary.maxVerticalG.value, thresholds.positiveVerticalG),
      bands: thresholds.positiveVerticalG,
      sampleValue: (sample) => sample.verticalG,
      low: false,
    },
    {
      metric: 'negative_vertical_g',
      peak: summary.minVerticalG,
      level: classifyLow(summary.minVerticalG.value, thresholds.negativeVerticalG),
      bands: thresholds.negativeVerticalG,
      sampleValue: (sample) => sample.verticalG,
      low: true,
    },
    {
      metric: 'lateral_g',
      peak: summary.maxAbsLateralG,
      level: classifyHigh(Math.abs(summary.maxAbsLateralG.value), thresholds.absoluteLateralG),
      bands: thresholds.absoluteLateralG,
      sampleValue: (sample) => Math.abs(sample.lateralG),
      low: false,
    },
    {
      metric: 'longitudinal_g',
      peak: summary.maxAbsLongitudinalG,
      level: classifyHigh(
        Math.abs(summary.maxAbsLongitudinalG.value),
        thresholds.absoluteLongitudinalG,
      ),
      bands: thresholds.absoluteLongitudinalG,
      sampleValue: (sample) => Math.abs(sample.longitudinalG),
      low: false,
    },
  ];
  const strongest = candidates.reduce((current, candidate) =>
    INTENSITY_RANK[candidate.level] > INTENSITY_RANK[current.level] ? candidate : current,
  );
  const sustainedThreshold =
    strongest.level === 'absurd'
      ? strongest.bands.absurd
      : strongest.level === 'extreme'
        ? strongest.bands.extreme
        : strongest.level === 'intense'
          ? strongest.bands.intense
          : strongest.bands.noticeable;
  const sustained =
    strongest.level === 'comfortable'
      ? 0
      : longestSustainedSeconds(summary.trace, (sample) => {
          const value = strongest.sampleValue(sample);
          return strongest.low ? value <= sustainedThreshold : value >= sustainedThreshold;
        });
  const level = sustained >= 2 ? promoteIntensity(strongest.level) : strongest.level;
  return {
    ...SUMMARIES[level],
    strongestMetric: strongest.metric,
    strongestDistanceM: strongest.peak.distance,
    strongestCarIndex: strongest.peak.carIndex,
    sustainedSeconds: sustained >= 2 ? sustained : null,
  };
}

export function simulatedIntensityScore(vector: GVector): number {
  const verticalScore =
    vector.vertical >= 0 ? Math.abs(vector.vertical) / 4 : Math.abs(vector.vertical) / 0.5;
  return Math.max(
    verticalScore,
    Math.abs(vector.lateral) / 0.75,
    Math.abs(vector.longitudinal) / 0.75,
  );
}
